import React, { useMemo } from 'react';
import { DateRangeFilter } from '@askporter/component-library/DateRangeFilter';
import { ExtendedMetadataFilters } from '@askporter/component-library/ExtendedMetadataFilters';
import { FilterSection } from '@askporter/component-library/FilterSection';
import { NewCheckboxFilterItem } from '@askporter/component-library/NewCheckboxFilterItem';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { UseQueryResult } from '@tanstack/react-query';
import { usePermissions } from '@askporter/auth-provider';
import { ManagedStateMultipleUserAutocomplete, ManagedStateMultipleTeamAutocomplete } from '@askporter/autocomplete';
import {
  SearchUserResultExternal,
  SearchTeamResultExternal,
  SearchAssetResultExternal,
  TaskStatusType,
  SearchTaskFilter,
  SlaCurrentStatusReadOnly,
  TaskTypeGroupListExternalTaskTypeGroups,
  SearchTaskStats,
  SearchExtendedMetadataFilterCriteria,
  ExtendedMetadataAttributeFilterSummaryExternal,
  SlaEscalationStatus,
} from '@askporter/client-grieg-lyric';
import { useConfig } from '@askporter/config-provider';
import { CU_FeatureFlags, ManagerRoles, toggleValueInList } from '@askporter/utils';
import type { DisplayedTaskFilters, TaskFilterPermissions } from '@askporter/utils';
import { TransformedTaskFilterValues, Actions, TaskFilterErrors, ConfigBasedTaskFilterKeys } from '../../types';
import { UnresolvedEntityRef, useUnresolvedSelectedEntityNames } from '../../utils/useUnresolvedSelectedEntityNames';
import { ConfigBasedFilter } from '../ConfigBasedFilter';
import { TaskTypesFilter } from '../TaskTypesFilter';
const PinnedOptionSkeleton: React.FC = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 16px' }}>
    <Skeleton variant="text" width="60%" height={20} sx={{ borderRadius: '4px' }} />
    <Skeleton
      variant="rounded"
      width={18}
      height={18}
      sx={{ borderRadius: '4px', flexShrink: 0, marginLeft: '12px' }}
    />
  </Box>
);
export interface SearchFilter<SearchResultsType> {
  searchValue: string;
  onChange?: (value: string) => void;
  searchResults: SearchResultsType;
  isLoading: boolean;
  hasMore?: boolean;
  fetchMore?: () => void;
  moreResultsLoading?: boolean;
  totalResults?: number;
}
export interface FilterSectionsProps {
  filters: SearchTaskFilter;
  t: (key: string, options?: Record<string, string | number>) => string;
  filterPermissions: TaskFilterPermissions;
  actions: Actions;
  dateTimeOnChange: (key: string, target: string, value: string | SearchExtendedMetadataFilterCriteria) => void;
  linkedUser?: SearchFilter<SearchUserResultExternal['results']>;
  assignedUser?: SearchFilter<SearchUserResultExternal['results']>;
  assignedTeam?: SearchFilter<SearchTeamResultExternal['results']>;
  linkedAsset?: SearchFilter<SearchAssetResultExternal['results']>;
  dueSlaStatuses?: {
    value: SlaCurrentStatusReadOnly;
    label: string;
    count: number;
  }[];
  responseSlaStatuses?: {
    value: SlaCurrentStatusReadOnly;
    label: string;
    count: number;
  }[];
  resolveSlaStatuses?: {
    value: SlaCurrentStatusReadOnly;
    label: string;
    count: number;
  }[];
  escalationStatuses?: {
    value: SlaEscalationStatus;
    label: string;
    count: number;
  }[];
  statusTypes?: {
    value: TaskStatusType;
    label: string;
  }[];
  configBasedTaskFilterValues: Record<ConfigBasedTaskFilterKeys, TransformedTaskFilterValues>;
  configBasedFilterSearch: Record<ConfigBasedTaskFilterKeys, string>;
  configBasedFilterSearchOnChange: (e: React.ChangeEvent<HTMLInputElement>, filterKey: string) => void;
  errors: TaskFilterErrors;
  isSmallDevice: boolean;
  areFilterCountsLoading: boolean;
  configBasedTaskFilterValuesLoading: boolean;
  taskTypesQuery: UseQueryResult<TaskTypeGroupListExternalTaskTypeGroups[], Error>;
  stats: SearchTaskStats;
  extendedMetadataFilters?: ExtendedMetadataAttributeFilterSummaryExternal[];
}
/**
 * Renders all filter sections for the tasks side bar
 * @param filters - filter state
 * @param t - translation function
 * @param filterPermissions - object describing the filter visibility
 * @param actions - reducer action functions
 * @param dateTimeOnChange - onChange handler for date time field
 * @param linkedUser - linked user object with searchValue string, onChange handler, and searchResults
 * @param assignedUser - assigned user object with searchValue string, onChange handler, and searchResults
 * @param assignedTeam - assigned team object with searchValue string, onChange handler, and searchResults
 * @param linkedAsset - linked asset object with searchValue string, onChange handler, and searchResults
 * @param responseSlaStatuses - array of response sla status value, labels and counts
 * @param resolveSlaStatuses - array of resolve sla status value, labels and counts
 * @param statusTypes -array of TakStatusTypes value and labels
 * @param configBasedTaskFilterValues - the retrieved values for config based task filters
 * @param configBasedFilterSearch - free text search state for config based filters
 * @param configBasedFilterSearchOnChange - onChange handler for config based filters
 * @param errors - object containing the error state of values retrieved from API calls
 * @param isSmallDevice - whether it's a small viewport or not
 * @param areFilterCountsLoading - whether we are still waiting for the filter counts to be calculated
 * @param configBasedTaskFilterValuesLoading - whether the GET /task-filter-values is loading
 * @param taskTypesQuery - the query object for task types, providing access to data, loading & error
 * @param extendedMetadataFilters - the extended metadata filters
 */
export const FilterSections: React.FC<React.PropsWithChildren<FilterSectionsProps>> = ({
  filters,
  t,
  filterPermissions,
  actions,
  dateTimeOnChange,
  linkedUser,
  assignedUser,
  assignedTeam,
  linkedAsset,
  statusTypes,
  configBasedTaskFilterValues,
  configBasedFilterSearch,
  configBasedFilterSearchOnChange,
  errors,
  areFilterCountsLoading,
  configBasedTaskFilterValuesLoading,
  taskTypesQuery,
  stats,
  extendedMetadataFilters,
  dueSlaStatuses,
  responseSlaStatuses,
  resolveSlaStatuses,
  escalationStatuses,
}: FilterSectionsProps) => {
  const { featureFlags } = useConfig();
  const showFilter: Record<DisplayedTaskFilters, boolean> = {
    linkedAsset: usePermissions(filterPermissions.linkedAsset),
    assignedUser: usePermissions(filterPermissions.assignedUser),
    assignedTeam: usePermissions(filterPermissions.assignedTeam),
    created: usePermissions(filterPermissions.created),
    dueStatus: usePermissions(filterPermissions.dueStatus),
    statusType: usePermissions(filterPermissions.statusType),
    priority: usePermissions(filterPermissions.priority),
    respondStatus: usePermissions(filterPermissions.respondStatus),
    resolveStatus: usePermissions(filterPermissions.resolveStatus),
    escalationFilters: usePermissions(filterPermissions.escalationFilters),
    status: usePermissions(filterPermissions.status),
    taskType: usePermissions(filterPermissions.taskType),
    extendedMetadata: usePermissions(filterPermissions.extendedMetadata),
    dueSlaStatus: usePermissions(filterPermissions?.dueSlaStatus),
  };
  const missingLinkedAssetUids = useMemo(
    () => filters.linkedAsset?.filter((uid) => !linkedAsset?.searchResults?.some((result) => result.uid === uid)) ?? [],
    [filters.linkedAsset, linkedAsset?.searchResults],
  );
  const missingLinkedUserUids = useMemo(
    () => filters.linkedUser?.filter((uid) => !linkedUser?.searchResults?.some((result) => result.uid === uid)) ?? [],
    [filters.linkedUser, linkedUser?.searchResults],
  );
  const missingAssignedUserUids = useMemo(
    () =>
      filters.assignedUser?.filter((uid) => !assignedUser?.searchResults?.some((result) => result.uid === uid)) ?? [],
    [filters.assignedUser, assignedUser?.searchResults],
  );
  const missingAssignedTeamUids = useMemo(
    () =>
      filters.assignedTeam?.filter((uid) => !assignedTeam?.searchResults?.some((result) => result.uid === uid)) ?? [],
    [filters.assignedTeam, assignedTeam?.searchResults],
  );
  const missingEntityRefs = useMemo(
    (): UnresolvedEntityRef[] => [
      ...missingLinkedAssetUids.map((uid): UnresolvedEntityRef => ({ type: 'asset', uid })),
      ...missingLinkedUserUids.map((uid): UnresolvedEntityRef => ({ type: 'user', uid })),
      ...missingAssignedUserUids.map((uid): UnresolvedEntityRef => ({ type: 'user', uid })),
      ...missingAssignedTeamUids.map((uid): UnresolvedEntityRef => ({ type: 'team', uid })),
    ],
    [missingLinkedAssetUids, missingLinkedUserUids, missingAssignedUserUids, missingAssignedTeamUids],
  );
  const resolvedMissingNames = useUnresolvedSelectedEntityNames(missingEntityRefs);
  return (
    <>
      {/* Linked assets */}
      {showFilter.linkedAsset && (
        <FilterSection
          filterKey="linkedAsset"
          isLoading={linkedAsset.isLoading}
          t={t}
          filterSectionTitle={t('ns.task_filter:section:linked_asset')}
          itemsCount={filters.linkedAsset?.length}
          searchLabel={t('ns.common:search:assets')}
          searchValue={linkedAsset.searchValue}
          searchOnChange={(e: React.ChangeEvent<HTMLInputElement>) => linkedAsset.onChange(e.currentTarget.value)}
          isExpandedOnMount={!!filters?.linkedAsset?.length}
          isError={errors.assetIsError}
          seeMoreBtn={{
            hasMore: linkedAsset.hasMore,
            totalResults: linkedAsset.totalResults,
            onSeeMore: () => linkedAsset.fetchMore(),
            isSeeMoreLoading: linkedAsset.moreResultsLoading,
          }}
        >
          {missingLinkedAssetUids.map((uid) =>
            resolvedMissingNames[uid]?.isLoading ? (
              <PinnedOptionSkeleton key={uid} />
            ) : (
              <NewCheckboxFilterItem
                key={uid}
                title={resolvedMissingNames[uid]?.name ?? uid}
                showTitleTooltip={true}
                isChecked={true}
                onChange={() => {
                  actions.setFilters({ linkedAsset: toggleValueInList(uid, filters.linkedAsset) });
                }}
              />
            ),
          )}
          {linkedAsset.searchResults?.map(({ uid, title }) => (
            <NewCheckboxFilterItem
              key={uid}
              title={title}
              showTitleTooltip={true}
              isChecked={filters.linkedAsset?.includes(uid)}
              onChange={() => {
                actions.setFilters({ linkedAsset: toggleValueInList(uid, filters.linkedAsset) });
              }}
            />
          ))}
        </FilterSection>
      )}
      {/* Linked users  */}
      <FilterSection
        filterKey="linkedUser"
        t={t}
        isLoading={linkedUser.isLoading}
        filterSectionTitle={t('ns.task_filter:section:linked_user')}
        itemsCount={filters?.linkedUser?.length}
        searchLabel={t('ns.task_filter:section:linked_user:search_label')}
        searchValue={linkedUser.searchValue}
        searchOnChange={(e: React.ChangeEvent<HTMLInputElement>) => linkedUser.onChange(e.currentTarget.value)}
        isExpandedOnMount={!!filters?.linkedUser?.length}
        isError={errors.userIsError}
        seeMoreBtn={{
          hasMore: linkedUser.hasMore,
          totalResults: linkedUser.totalResults,
          onSeeMore: () => linkedUser.fetchMore(),
          isSeeMoreLoading: linkedUser.moreResultsLoading,
        }}
      >
        {missingLinkedUserUids.map((uid) =>
          resolvedMissingNames[uid]?.isLoading ? (
            <PinnedOptionSkeleton key={uid} />
          ) : (
            <NewCheckboxFilterItem
              key={uid}
              title={resolvedMissingNames[uid]?.name ?? uid}
              isChecked={true}
              onChange={() => {
                actions.setFilters({ linkedUser: toggleValueInList(uid, filters.linkedUser) });
              }}
            />
          ),
        )}
        {linkedUser.searchResults?.map(({ givenName, familyName, uid }) => (
          <NewCheckboxFilterItem
            key={uid}
            title={`${givenName} ${familyName}`}
            isChecked={filters.linkedUser?.includes(uid)}
            onChange={() => {
              actions.setFilters({ linkedUser: toggleValueInList(uid, filters.linkedUser) });
            }}
          />
        ))}
      </FilterSection>
      {/* Assigned users  */}
      {showFilter.assignedUser && (
        <FilterSection
          filterKey="assignedUser"
          t={t}
          isLoading={assignedUser.isLoading}
          filterSectionTitle={t('ns.task_filter:section:assigned_user')}
          itemsCount={filters?.assignedUser?.length}
          searchLabel={t('ns.task_filter:section:assigned_user:search_label')}
          searchValue={assignedUser.searchValue}
          searchOnChange={(e: React.ChangeEvent<HTMLInputElement>) => assignedUser.onChange(e.currentTarget.value)}
          isExpandedOnMount={!!filters?.assignedUser?.length}
          isError={errors.userIsError}
          seeMoreBtn={{
            hasMore: assignedUser.hasMore,
            totalResults: assignedUser.totalResults,
            onSeeMore: () => assignedUser.fetchMore(),
            isSeeMoreLoading: assignedUser.moreResultsLoading,
          }}
        >
          {missingAssignedUserUids.map((uid) =>
            resolvedMissingNames[uid]?.isLoading ? (
              <PinnedOptionSkeleton key={uid} />
            ) : (
              <NewCheckboxFilterItem
                key={uid}
                title={resolvedMissingNames[uid]?.name ?? uid}
                isChecked={true}
                onChange={() => {
                  actions.setFilters({ assignedUser: toggleValueInList(uid, filters.assignedUser) });
                }}
              />
            ),
          )}
          {assignedUser.searchResults?.map(({ givenName, familyName, uid }) => (
            <NewCheckboxFilterItem
              key={uid}
              title={`${givenName} ${familyName}`}
              isChecked={filters.assignedUser?.includes(uid)}
              onChange={() => {
                actions.setFilters({ assignedUser: toggleValueInList(uid, filters.assignedUser) });
              }}
            />
          ))}
        </FilterSection>
      )}
      {/* Assigned teams  */}
      {showFilter.assignedTeam && (
        <FilterSection
          filterKey="assignedTeam"
          isLoading={assignedTeam.isLoading}
          t={t}
          filterSectionTitle={t('ns.task_filter:section:assigned_team')}
          itemsCount={filters?.assignedTeam?.length}
          searchLabel={t('ns.task_filter:section:assigned_team:search_label')}
          searchValue={assignedTeam.searchValue}
          searchOnChange={(e: React.ChangeEvent<HTMLInputElement>) => assignedTeam.onChange(e.currentTarget.value)}
          isExpandedOnMount={!!filters?.assignedTeam?.length}
          isError={errors.teamIsError}
          seeMoreBtn={{
            hasMore: assignedTeam.hasMore,
            totalResults: assignedTeam.totalResults,
            onSeeMore: () => assignedTeam.fetchMore(),
            isSeeMoreLoading: assignedTeam.moreResultsLoading,
          }}
        >
          {missingAssignedTeamUids.map((uid) =>
            resolvedMissingNames[uid]?.isLoading ? (
              <PinnedOptionSkeleton key={uid} />
            ) : (
              <NewCheckboxFilterItem
                key={uid}
                title={resolvedMissingNames[uid]?.name ?? uid}
                showTitleTooltip={true}
                isChecked={true}
                onChange={() => {
                  actions.setFilters({ assignedTeam: toggleValueInList(uid, filters.assignedTeam) });
                }}
              />
            ),
          )}
          {assignedTeam.searchResults?.map(({ name, uid }) => (
            <NewCheckboxFilterItem
              key={uid}
              title={name}
              showTitleTooltip={true}
              isChecked={filters.assignedTeam?.includes(uid)}
              onChange={() => {
                actions.setFilters({ assignedTeam: toggleValueInList(uid, filters.assignedTeam) });
              }}
            />
          ))}
        </FilterSection>
      )}
      {/* Created date */}
      {showFilter.created && (
        <FilterSection
          filterKey="created"
          t={t}
          filterSectionTitle={t('ns.task_filter:section:created')}
          isExpandedOnMount={!!(filters?.created?.after || filters?.created?.before)}
        >
          <DateRangeFilter
            t={t}
            isUrlQuerySensitive
            filterKey="created"
            afterValue={filters?.created?.after}
            beforeValue={filters?.created?.before}
            onChange={({ value, target }) => dateTimeOnChange('created', target, value)}
          />
        </FilterSection>
      )}
      {/* Due status */}
      {showFilter.dueStatus && (
        <FilterSection
          filterKey="dueStatus"
          t={t}
          filterSectionTitle={t('task_filter:section:sla:due_status:label')}
          itemsCount={filters?.dueSlaStatus?.length}
          isExpandedOnMount={
            !!(filters?.resolutionDueDate?.after || filters?.resolutionDueDate?.before || filters?.dueSlaStatus?.length)
          }
        >
          <DateRangeFilter
            t={t}
            isUrlQuerySensitive
            filterKey="dueStatus"
            controls={['calendar']}
            afterValue={filters?.resolutionDueDate?.after}
            beforeValue={filters?.resolutionDueDate?.before}
            onChange={({ value, target }) => {
              actions.setFilters({
                resolutionDueDate: {
                  ...filters.resolutionDueDate,
                  [target]: value,
                },
              });
            }}
          />
          {dueSlaStatuses.map(({ label, value: status, count }) => {
            if (status === 'NA' || status === 'PAUSED') return null;
            const { dueSlaStatus = [] } = filters;
            const isChecked = dueSlaStatus?.includes(status);
            return (
              <NewCheckboxFilterItem
                key={`task_filter_section_due_status_${status}`}
                title={label}
                count={count}
                isChecked={isChecked}
                onChange={() => {
                  actions.setFilters(
                    isChecked
                      ? {
                          dueSlaStatus: [...dueSlaStatus?.filter((value) => value !== status)],
                        }
                      : {
                          dueSlaStatus: [...dueSlaStatus, status],
                        },
                  );
                }}
              />
            );
          })}
        </FilterSection>
      )}
      {/* Status type (Open/Closed) */}
      {showFilter.statusType && (
        <FilterSection
          filterKey="statusType"
          t={t}
          isLoading={areFilterCountsLoading}
          filterSectionTitle={t('ns.task_filter:section:status_type')}
          itemsCount={filters?.statusType?.length}
          isExpandedOnMount={!!filters?.statusType?.length}
        >
          {statusTypes?.map(({ value, label, count }: { value: TaskStatusType; label: string; count: number }) => (
            <NewCheckboxFilterItem
              key={`task_filter_section_status_type_${value}`}
              title={label}
              count={count}
              isChecked={filters.statusType?.includes(value)}
              onChange={() => {
                actions.setFilters({ statusType: toggleValueInList(value, filters.statusType) });
              }}
            />
          ))}
        </FilterSection>
      )}
      {/* Priority */}
      {showFilter.priority && (
        <ConfigBasedFilter
          filters={filters}
          isLoading={areFilterCountsLoading || configBasedTaskFilterValuesLoading}
          filterKey="priority"
          filterSearch={configBasedFilterSearch}
          searchOnChange={configBasedFilterSearchOnChange}
          t={t}
          taskFilterValues={configBasedTaskFilterValues}
          setFilters={actions.setFilters}
          isError={errors.taskFilterValuesIsError}
        />
      )}
      {/* Respond status */}
      {showFilter.respondStatus && (
        <FilterSection
          filterKey="respondStatus"
          t={t}
          filterSectionTitle={t('task_filter:section:sla:respond_status:label')}
          itemsCount={filters?.responseSlaStatus?.length}
          isExpandedOnMount={
            !!(
              filters?.responseDueDateTime?.after ||
              filters?.responseDueDateTime?.before ||
              filters?.responseSlaStatus?.length
            )
          }
        >
          <DateRangeFilter
            t={t}
            isUrlQuerySensitive
            filterKey="respondStatus"
            afterValue={filters?.responseDueDateTime?.after}
            beforeValue={filters?.responseDueDateTime?.before}
            onChange={({ value, target }) => {
              actions.setFilters({
                responseDueDateTime: {
                  ...filters.responseDueDateTime,
                  [target]: value,
                },
              });
            }}
          />
          {responseSlaStatuses.map(({ label, value: status, count }) => {
            if (status === 'NA') return null;
            const { responseSlaStatus = [] } = filters;
            const isChecked = responseSlaStatus?.includes(status);
            return (
              <NewCheckboxFilterItem
                key={`task_filter_section_respond_status_${status}`}
                title={label}
                count={count}
                isChecked={isChecked}
                onChange={() => {
                  actions.setFilters(
                    isChecked
                      ? {
                          responseSlaStatus: [...responseSlaStatus?.filter((value) => value !== status)],
                        }
                      : {
                          responseSlaStatus: [...responseSlaStatus, status],
                        },
                  );
                }}
              />
            );
          })}
        </FilterSection>
      )}
      {/* Resolve status */}
      {showFilter.resolveStatus && (
        <FilterSection
          filterKey="resolveStatus"
          t={t}
          filterSectionTitle={t('task_filter:section:sla:resolve_status:label')}
          itemsCount={filters?.resolutionSlaStatus?.length}
          isExpandedOnMount={
            !!(
              filters?.resolutionDueDateTime?.after ||
              filters?.resolutionDueDateTime?.before ||
              filters?.resolutionSlaStatus?.length
            )
          }
        >
          <DateRangeFilter
            t={t}
            isUrlQuerySensitive
            filterKey="resolveStatus"
            afterValue={filters?.resolutionDueDateTime?.after}
            beforeValue={filters?.resolutionDueDateTime?.before}
            onChange={({ value, target }) => {
              actions.setFilters({
                resolutionDueDateTime: {
                  ...filters.resolutionDueDateTime,
                  [target]: value,
                },
              });
            }}
          />
          {resolveSlaStatuses.map(({ label, value: status, count }) => {
            if (status === 'NA') return null;
            const { resolutionSlaStatus = [] } = filters;
            const isChecked = resolutionSlaStatus?.includes(status);
            return (
              <NewCheckboxFilterItem
                key={`task_filter_section_resolve_status_${status}`}
                title={label}
                count={count}
                isChecked={isChecked}
                onChange={() => {
                  actions.setFilters(
                    isChecked
                      ? {
                          resolutionSlaStatus: [...resolutionSlaStatus?.filter((value) => value !== status)],
                        }
                      : {
                          resolutionSlaStatus: [...resolutionSlaStatus, status],
                        },
                  );
                }}
              />
            );
          })}
        </FilterSection>
      )}
      {featureFlags?.[CU_FeatureFlags.ENABLE_TASK_ESCALATION] && showFilter.escalationFilters && (
        <FilterSection
          filterKey="escalationStatus"
          t={t}
          filterSectionTitle={t('task_filter:section:escalation_status:label')}
          isExpandedOnMount={
            filters?.escalationStatus?.length > 0 ||
            filters?.escalatedToUser?.length > 0 ||
            filters?.escalatedToTeam?.length > 0
          }
        >
          {escalationStatuses.map(({ label, value, count }) => {
            if (value === 'NONE') return null;
            const { escalationStatus = [] } = filters;
            const isChecked = escalationStatus?.includes(value);
            return (
              <NewCheckboxFilterItem
                key={`task_filter_section_escalation_status_${value}`}
                title={label}
                count={count}
                isChecked={isChecked}
                onChange={() => {
                  actions.setFilters({ escalationStatus: toggleValueInList(value, filters.escalationStatus) });
                }}
              />
            );
          })}
          <ManagedStateMultipleUserAutocomplete
            idPrefix="escalatedToUser"
            t={t}
            label={t('task_filter:section:escalated_to_user:label')}
            fullWidth
            filter={{
              role: ManagerRoles,
            }}
            sx={{ paddingX: 4 }}
            selectedUserUids={filters?.escalatedToUser}
            onChange={(value) => {
              actions.setFilters({
                escalatedToUser: value?.map((user) => user?.uid),
              });
            }}
          />
          <ManagedStateMultipleTeamAutocomplete
            idPrefix="escalatedToTeam"
            t={t}
            label={t('task_filter:section:escalated_to_team:label')}
            fullWidth
            sx={{ paddingX: 4 }}
            selectedTeamUids={filters?.escalatedToTeam}
            onChange={(value) => {
              actions.setFilters({
                escalatedToTeam: value?.map((team) => team?.uid),
              });
            }}
          />
        </FilterSection>
      )}
      {/* Status */}
      {showFilter.status && (
        <ConfigBasedFilter
          filters={filters}
          isLoading={areFilterCountsLoading || configBasedTaskFilterValuesLoading}
          filterKey="status"
          filterSearch={configBasedFilterSearch}
          searchOnChange={configBasedFilterSearchOnChange}
          t={t}
          taskFilterValues={configBasedTaskFilterValues}
          setFilters={actions.setFilters}
          isError={errors.taskFilterValuesIsError}
        />
      )}
      {/* TaskType */}
      {showFilter.taskType && (
        <TaskTypesFilter
          filter={filters?.taskType}
          stats={stats}
          setFilters={actions.setFilters}
          taskTypesQuery={taskTypesQuery}
          t={t}
        />
      )}
      {/* Extended metadata */}
      {showFilter.extendedMetadata && extendedMetadataFilters?.length > 0 ? (
        <ExtendedMetadataFilters
          extendedMetadataFilters={extendedMetadataFilters}
          filterCountsLoading={areFilterCountsLoading}
          filters={filters}
          setFilters={actions.setFilters}
          stats={stats}
          t={t}
        />
      ) : null}
    </>
  );
};
